import { createFileRoute, useRouter } from "@tanstack/react-router";
import {
  useBusinessHomeSummary,
  type BusinessHomeSummary,
} from "~/services/business/main";
import { useCompanyCreditWallet } from "~/services/business/credit";
import { useBusinessNoticeHasNewReply } from "~/services/business/notice";
import { useBusinessAnnouncementUnreadCount } from "~/services/business/announcement";
import { useBusinessAuthUserInfo } from "~/services/business/auth";
import { useBusinessMypageCompanyInfo } from "~/services/business/myPage";
import { TermsNoticeBanner } from "@/common/TermsNoticeBanner";
import { IconSchool } from "@/Icon/School";
import { IconTicket } from "@/Icon/Ticket";
import { IconNotice } from "@/Icon/Notice";
import { IconGallery } from "@/Icon/Gallery";
import { IconCalendarPlus } from "@/Icon/CalendarPlus";

export const Route = createFileRoute("/business/layout/home")({
  component: BusinessHome,
});

const DAY_LABELS = ["일", "월", "화", "수", "목", "금", "토"];

function BusinessHome() {
  const { history } = useRouter();
  const { data: userInfo } = useBusinessAuthUserInfo();
  const isOwner = !!userInfo?.isOwner;

  // company-info 는 원장 전용 API. 선생님이 호출하면 4001 이 떨어지므로 막는다.
  const { data: companyInfo } = useBusinessMypageCompanyInfo(isOwner);
  const { data: summary } = useBusinessHomeSummary();
  const { data: newReply } = useBusinessNoticeHasNewReply();
  const { data: announcementUnread } = useBusinessAnnouncementUnreadCount();

  const hasNewNoticeReply = !!(
    newReply?.data?.hasRecipientNewReply || newReply?.data?.hasSenderNewReply
  );
  const hasUnreadAnnouncement = (announcementUnread?.count ?? 0) > 0;
  const pendingReserve = summary?.pendingReservationCount ?? 0;
  const pendingContract = summary?.pendingContractCount ?? 0;
  const hasTodo =
    pendingReserve > 0 ||
    pendingContract > 0 ||
    hasNewNoticeReply ||
    hasUnreadAnnouncement;

  return (
    <div className="flex-auto bg-[#F5F5F5]">
      <header className="sticky top-0 z-10 flex h-14 items-center bg-white px-4">
        <div className="truncate text-base font-bold">
          {companyInfo?.companyName ?? "우리 매장"}
        </div>
      </header>

      <div className="space-y-5 px-4 py-4">
        {/* 약관 개정 고지 — 고지 기간에만 노출 */}
        <TermsNoticeBanner targetRole="BUSINESS" />

        {/* 오늘 요약 */}
        <section>
          <h2 className="mb-2 text-sm font-bold">오늘 현황</h2>
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={() => history.push("/business/layout/calendar")}
              className="rounded-xl bg-white py-3 text-center"
            >
              <div className="text-xs text-gray">오늘 등원</div>
              <div className="text-xl font-bold">
                {summary?.attend?.totalCount ?? "-"}
              </div>
            </button>
            <button
              onClick={() =>
                history.push("/business/layout/to-school?attendanceType=등원")
              }
              className="rounded-xl bg-white py-3 text-center"
            >
              <div className="text-xs text-gray">등원 현황</div>
              <div className="text-xl font-bold">
                {summary?.attend?.completeCount ?? "-"}
                <span className="text-xs font-medium text-gray">
                  /{summary?.attend?.totalCount ?? "-"}
                </span>
              </div>
            </button>
            <button
              onClick={() =>
                history.push("/business/layout/to-school?attendanceType=하원")
              }
              className="rounded-xl bg-white py-3 text-center"
            >
              <div className="text-xs text-gray">하원 현황</div>
              <div className="text-xl font-bold">
                {summary?.dismiss?.completeCount ?? "-"}
                <span className="text-xs font-medium text-gray">
                  /{summary?.dismiss?.totalCount ?? "-"}
                </span>
              </div>
            </button>
          </div>

          {/* 오늘 등원 반려동물 아바타 */}
          {summary && summary.todayPets.length > 0 && (
            <div className="mt-2 flex items-center gap-2 overflow-x-auto rounded-xl bg-white p-3">
              {summary.todayPets.map((pet) => (
                <button
                  key={pet.petSeq}
                  type="button"
                  disabled={!pet.reservationSeq}
                  onClick={() =>
                    pet.reservationSeq &&
                    history.push(
                      `/business/layout/revservation?reservationSeq=${pet.reservationSeq}`
                    )
                  }
                  className="flex w-12 shrink-0 flex-col items-center gap-1"
                >
                  <div className="relative h-10 w-10">
                    <div className="h-10 w-10 overflow-hidden rounded-full bg-[#F5F5F5]">
                      {pet.thumbnailUrl && (
                        <img
                          src={pet.thumbnailUrl}
                          alt={pet.petName}
                          className="h-full w-full object-cover"
                        />
                      )}
                    </div>
                    <span
                      className={`absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-white ${
                        pet.cdAttendStatus === "ATTEND_STATUS_ATTENDED"
                          ? "bg-[#3DCB7F]"
                          : pet.cdAttendStatus === "ATTEND_STATUS_DISMISSED"
                            ? "bg-[#999999]"
                            : "bg-[#FF9934]"
                      }`}
                    />
                  </div>
                  <span className="w-full truncate text-center text-[10px] text-gray">
                    {pet.petName}
                  </span>
                </button>
              ))}
            </div>
          )}

          <button
            onClick={() => history.push("/business/layout/to-school")}
            className="mt-2 flex w-full items-center justify-between rounded-xl bg-white p-3"
          >
            <span className="flex items-center gap-2 text-sm font-bold">
              <IconSchool active />
              등하원 체크하기
            </span>
            <span className="text-gray">›</span>
          </button>
        </section>

        {/* 알림 */}
        <section>
          <h2 className="mb-2 text-sm font-bold">알림</h2>
          {hasTodo ? (
            <div className="overflow-hidden rounded-xl bg-white">
              {pendingReserve > 0 && (
                <button
                  onClick={() =>
                    history.push("/business/layout/calendar?tab=승인 대기")
                  }
                  className="flex w-full items-center justify-between border-b border-[#F0F0F0] p-3"
                >
                  <span className="text-sm">예약 승인 대기</span>
                  <span className="rounded-full bg-[#FDECEC] px-2 py-0.5 text-xs font-bold text-[#E24B4A]">
                    {pendingReserve}건
                  </span>
                </button>
              )}
              {hasNewNoticeReply && (
                <button
                  onClick={() => history.push("/business/layout/notify")}
                  className="flex w-full items-center justify-between border-b border-[#F0F0F0] p-3"
                >
                  <span className="text-sm">새로운 알림장 메시지</span>
                  <span className="rounded-full bg-[#FFF3E5] px-2 py-0.5 text-xs font-bold text-yellow">
                    확인
                  </span>
                </button>
              )}
              {hasUnreadAnnouncement && (
                <button
                  onClick={() =>
                    history.push("/business/layout/user-owner-announcement")
                  }
                  className="flex w-full items-center justify-between border-b border-[#F0F0F0] p-3"
                >
                  <span className="text-sm">공지사항</span>
                  <span className="rounded-full bg-[#FFF3E5] px-2 py-0.5 text-xs font-bold text-yellow">
                    확인
                  </span>
                </button>
              )}
              {pendingContract > 0 && (
                <button
                  onClick={() =>
                    history.push("/business/layout/contract-status")
                  }
                  className="flex w-full items-center justify-between p-3"
                >
                  <span className="text-sm">계약 진행 중</span>
                  <span className="rounded-full bg-[#EEF4FD] px-2 py-0.5 text-xs font-bold text-[#378ADD]">
                    {pendingContract}건
                  </span>
                </button>
              )}
            </div>
          ) : (
            <div className="rounded-xl bg-white p-4 text-center text-sm text-gray">
              새로운 알림이 없어요
            </div>
          )}
        </section>

        {/* 크레딧 / 통계 — 원장 전용 */}
        {isOwner && (
          <OwnerFinanceSection
            expiringTicketPetCount={summary?.expiringTicketPetCount ?? 0}
          />
        )}

        {/* 주간 예약 추이 */}
        {summary && summary.weeklyReservations.length > 0 && (
          <section>
            <h2 className="mb-2 text-sm font-bold">최근 7일 예약</h2>
            <WeeklyChart items={summary.weeklyReservations} />
          </section>
        )}

        {/* 바로가기 */}
        <section>
          <h2 className="mb-2 text-sm font-bold">바로가기</h2>
          <div className="grid grid-cols-4 gap-2">
            <button
              onClick={() =>
                history.push("/business/layout/user-subscription-issue")
              }
              className="flex flex-col items-center gap-1.5 rounded-xl bg-white py-3"
            >
              <span className="flex h-7 items-center justify-center">
                <IconTicket active size={28} />
              </span>
              <span className="text-[11px] font-medium">이용권 발급</span>
            </button>
            <button
              onClick={() => history.push("/business/layout/notify-send")}
              className="flex flex-col items-center gap-1.5 rounded-xl bg-white py-3"
            >
              <span className="flex h-7 items-center justify-center">
                <IconNotice active />
              </span>
              <span className="text-[11px] font-medium">알림장 쓰기</span>
            </button>
            <button
              onClick={() => history.push("/business/layout/gallery-add")}
              className="flex flex-col items-center gap-1.5 rounded-xl bg-white py-3"
            >
              <span className="flex h-7 items-center justify-center">
                <IconGallery active />
              </span>
              <span className="text-[11px] font-medium">사진 올리기</span>
            </button>
            <button
              onClick={() => history.push("/business/layout/manual-reserve")}
              className="flex flex-col items-center gap-1.5 rounded-xl bg-white py-3"
            >
              <span className="flex h-7 items-center justify-center">
                <IconCalendarPlus active size={26} />
              </span>
              <span className="text-[11px] font-medium">수기 예약</span>
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}

/** 크레딧 잔액 + 서비스 통계 진입 (원장 전용 — 매출 숫자는 홈에 노출하지 않는다) */
function OwnerFinanceSection({
  expiringTicketPetCount,
}: {
  expiringTicketPetCount: number;
}) {
  const { history } = useRouter();
  const { data: wallet } = useCompanyCreditWallet();

  return (
    <section>
      <h2 className="mb-2 text-sm font-bold">운영</h2>
      <div className="grid grid-cols-2 gap-2">
        <button
          onClick={() => history.push("/business/layout/user-credit-wallet")}
          className="rounded-xl bg-white p-3 text-left"
        >
          <div className="text-xs text-gray">크레딧 잔액</div>
          <div className="mt-0.5 text-lg font-bold">
            {wallet ? wallet.balance.toLocaleString() : "-"}
          </div>
          <div className="mt-0.5 text-[11px] text-yellow">충전하기 ›</div>
        </button>
        <button
          onClick={() =>
            history.push("/business/layout/user-service-statistics")
          }
          className="rounded-xl bg-white p-3 text-left"
        >
          <div className="text-xs text-gray">매출 · 정산</div>
          <div className="mt-0.5 text-lg font-bold">서비스 통계</div>
          <div className="mt-0.5 text-[11px] text-gray">자세히 보기 ›</div>
        </button>
        <button
          onClick={() =>
            history.push(
              "/business/layout/user-subscription-issue-history?status=EXPIRING",
            )
          }
          className="col-span-2 rounded-xl bg-white p-3 text-left"
        >
          <div className="text-xs text-gray">유효기간 만료 예정 이용권</div>
          <div className="mt-0.5 flex items-baseline gap-1">
            <span
              className={`text-lg font-bold ${
                expiringTicketPetCount > 0 ? "text-red-500" : ""
              }`}
            >
              {expiringTicketPetCount}
            </span>
            <span className="text-sm font-bold">마리</span>
          </div>
          <div className="mt-0.5 text-[11px] text-gray">이용권 확인하기 ›</div>
        </button>
      </div>
    </section>
  );
}

/** 최근 7일 예약 수 미니 바 차트 */
function WeeklyChart({
  items,
}: {
  items: BusinessHomeSummary["weeklyReservations"];
}) {
  const max = Math.max(1, ...items.map((it) => it.count));

  return (
    <div className="rounded-xl bg-white p-3">
      <div className="flex h-20 gap-1.5">
        {items.map((it, idx) => {
          const d = new Date(it.date);
          const isToday = idx === items.length - 1;
          const isClosed = !!it.closed;
          const heightPct = Math.max(4, Math.round((it.count / max) * 100));
          return (
            <div
              key={it.date}
              className="flex flex-1 flex-col items-center gap-1"
            >
              <span
                className={`text-[10px] font-bold ${isClosed ? "text-gray" : "text-black"}`}
              >
                {isClosed ? "휴" : it.count}
              </span>
              <div className="flex w-full flex-1 items-end">
                <div
                  className={`w-full rounded-t ${
                    isClosed
                      ? "bg-[#EDEDED]"
                      : isToday
                        ? "bg-yellow"
                        : "bg-[#FFE3C2]"
                  }`}
                  style={{ height: isClosed ? "4%" : `${heightPct}%` }}
                />
              </div>
              <span
                className={`text-[10px] ${isToday ? "font-bold text-yellow" : "text-gray"}`}
              >
                {isToday ? "오늘" : DAY_LABELS[d.getDay()]}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
